/**
 * ADR-0236 — ops2026 Trust engine → Readiness: per site and agent, progress
 * toward the design-doc §7 targets (false accusations ≤ 0.1% at the 95%
 * upper bound over verified network-days, impersonator precision ≥ 99.5%
 * at the 95% lower bound, minimum verified volume, no open drift alarm),
 * what is missing in plain words, and the per-site "Turn on labels"
 * control: enabled only when the site meets the targets; otherwise a staff
 * override that needs the site's domain typed and a reason (audited
 * server-side). The global kill switch (panel above) still wins.
 */

import { useCallback, useEffect, useState } from 'react';

import type { ReadinessStatus } from '@sill/types';
import type { SiteReadinessWire, TrustReadinessResponse } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatCount } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { formatRate } from './trust-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
export const FORCE_REASON_MIN = 10;
export const FORCE_REASON_MAX = 500;

export const READINESS_LABEL: Readonly<Record<ReadinessStatus, string>> = {
  meets: 'Meets targets',
  failing: 'Failing',
  not_enough_data: 'Not enough data',
};
const TONE: Readonly<Record<ReadinessStatus, 'lime' | 'coral' | 'neutral'>> = { meets: 'lime', failing: 'coral', not_enough_data: 'neutral' };

function SiteCard({ site, onChanged }: { site: SiteReadinessWire; onChanged: () => void }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [override, setOverride] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const label = site.site_domain ?? site.site_id;
  const confirmOk = confirm.trim().toLowerCase() === label.toLowerCase();
  const reasonOk = reason.trim().length >= FORCE_REASON_MIN && reason.trim().length <= FORCE_REASON_MAX;

  const run = (fn: () => Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    fn()
      .then(() => { setOverride(false); setConfirm(''); setReason(''); onChanged(); })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 409) setError('Not ready: the site does not meet the targets (the numbers may have changed). Use the override if you must.');
        else setError(err instanceof ApiError ? err.message : 'The change did not save.');
      })
      .finally(() => setBusy(false));
  };
  const btn = (primary: boolean, disabled: boolean): React.CSSProperties => ({
    fontFamily: sansFont, fontSize: 12, padding: '5px 10px', borderRadius: 3, cursor: disabled ? 'default' : 'pointer',
    background: primary ? C.text : 'transparent', color: primary ? C.bg : C.body, border: `1px solid ${primary ? C.text : C.border}`,
    opacity: disabled ? 0.5 : 1,
  });
  const th = { textAlign: 'right' as const, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const };
  const td = { padding: '6px 8px', color: C.body, borderBottom: `1px solid ${C.border}`, fontFamily: monoFont, fontSize: 11.5, textAlign: 'right' as const };

  return (
    <Card style={{ padding: 16, marginBottom: 12 }} data-testid={`readiness-${site.site_id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>{label}</span>
        <Pill tone={TONE[site.status]}>{READINESS_LABEL[site.status]}</Pill>
        {site.verdicts_live ? <Pill tone="sky">labels on</Pill> : null}
      </div>

      {site.agents.length > 0 ? (
        <TableScroll minWidth={720}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5, marginTop: 10 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Agent</th>
                <th style={th}>Network-days</th>
                <th style={th}>Misses</th>
                <th style={th}>95% upper</th>
                <th style={th}>Labelled</th>
                <th style={th}>Precision 95% lower</th>
                <th style={th}>Verified req. / days</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {site.agents.map((a) => (
                <tr key={a.agent_id}>
                  <td style={{ ...td, textAlign: 'left', fontFamily: sansFont, fontSize: 12.5 }}>{a.name}</td>
                  <td style={td}>{formatCount(a.false_accusation.network_days)}</td>
                  <td style={td}>{formatCount(a.false_accusation.misses)}</td>
                  <td style={td}>{formatRate(a.false_accusation.upper95)}</td>
                  <td style={td}>{formatCount(a.precision.labelled)}</td>
                  <td style={td}>{a.precision.lower95 === null ? 'no labels' : formatRate(a.precision.lower95)}</td>
                  <td style={td}>{formatCount(a.volume.verified_obs)} / {formatCount(a.volume.active_days)}</td>
                  <td style={{ ...td, textAlign: 'right' }}><Pill tone={TONE[a.status]}>{READINESS_LABEL[a.status]}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}

      {site.missing_text.length > 0 ? (
        <ul style={{ fontFamily: sansFont, fontSize: 12.5, color: C.body, margin: '10px 0 0', paddingLeft: 18, lineHeight: 1.6 }} data-testid={`missing-${site.site_id}`}>
          {site.missing_text.slice(0, 12).map((t) => <li key={t}>{t}</li>)}
          {site.missing_text.length > 12 ? <li>…and {site.missing_text.length - 12} more.</li> : null}
        </ul>
      ) : null}

      {error !== null ? <div role="alert" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.coralFg, marginTop: 10 }}>{error}</div> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {site.verdicts_live ? (
          <button type="button" disabled={busy} style={btn(false, busy)} onClick={() => run(() => api.setTrustVerdictFlag(site.site_id, false))} data-testid={`labels-off-${site.site_id}`}>
            Turn labels off
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={busy || site.status !== 'meets'}
              style={btn(true, busy || site.status !== 'meets')}
              onClick={() => run(() => api.setTrustVerdictFlag(site.site_id, true))}
              data-testid={`labels-on-${site.site_id}`}
              title={site.status === 'meets' ? undefined : 'Available when the site meets the targets'}
            >
              Turn on labels
            </button>
            {site.status !== 'meets' && !override ? (
              <button type="button" style={btn(false, false)} onClick={() => setOverride(true)} data-testid={`override-open-${site.site_id}`}>
                Override…
              </button>
            ) : null}
          </>
        )}
      </div>

      {override && !site.verdicts_live ? (
        <div style={{ marginTop: 12, padding: 12, border: `1px solid ${C.border}`, borderRadius: 3 }} data-testid={`override-${site.site_id}`}>
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.amberFg, lineHeight: 1.5 }}>
            This site does not meet the targets. Turning labels on anyway is recorded in the staff log with your reason. The global kill switch still overrides it.
          </div>
          <label style={{ display: 'block', fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 10 }}>
            Type <span style={{ fontFamily: monoFont, color: C.text }}>{label}</span> to confirm
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-label="Type the site's domain to confirm"
              data-testid={`override-confirm-${site.site_id}`}
              style={{ display: 'block', marginTop: 4, fontFamily: monoFont, fontSize: 12, padding: '5px 8px', background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, width: 320, maxWidth: '100%' }}
            />
          </label>
          <label style={{ display: 'block', fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 8 }}>
            Reason ({FORCE_REASON_MIN}–{FORCE_REASON_MAX} characters)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={FORCE_REASON_MAX}
              rows={2}
              aria-label="Reason for the override"
              data-testid={`override-reason-${site.site_id}`}
              style={{ display: 'block', marginTop: 4, fontFamily: sansFont, fontSize: 12, padding: 6, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, width: '100%', maxWidth: 480 }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              disabled={busy || !confirmOk || !reasonOk}
              style={btn(true, busy || !confirmOk || !reasonOk)}
              onClick={() => run(() => api.setTrustVerdictFlag(site.site_id, true, { confirm: confirm.trim(), reason: reason.trim() }))}
              data-testid={`override-submit-${site.site_id}`}
            >
              Turn on labels anyway
            </button>
            <button type="button" style={btn(false, false)} onClick={() => setOverride(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export function ReadinessPanel({ reloadToken = 0, onChanged }: { reloadToken?: number; onChanged?: () => void }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [data, setData] = useState<TrustReadinessResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.getTrustReadiness());
      setMissing(false);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'not_found') setMissing(true);
      else setError(err instanceof ApiError ? err.message : 'Failed to load readiness.');
    }
  }, [api]);

  useEffect(() => { void load(); }, [load, reloadToken]);

  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const, marginBottom: 8, marginTop: 24 };
  return (
    <div data-testid="readiness-panel">
      <div style={kicker}>Readiness · go-live gate per site</div>
      {missing ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Not available until the API release that adds it is live.</div>
        </Card>
      ) : null}
      {error !== null ? <div role="alert" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.coralFg }}>{error}</div> : null}
      {data !== null ? (
        <>
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
            Measured per site under <span style={{ fontFamily: monoFont, color: C.body }}>{data.rule_version}</span>, from that site's own traffic only.
            Targets per agent: false accusations ≤ {formatRate(data.targets.false_accusation_upper95)} at the 95% upper bound
            ({formatCount(data.targets.network_days_at_zero_misses)} verified network-days with no misses), impersonator precision ≥ {formatRate(data.targets.impersonator_precision_lower95)} at the 95% lower bound,
            at least {formatCount(data.targets.min_verified_requests)} verified requests over {data.targets.min_active_days} days, and no open drift alarm.
            A network-day is one network the agent was proven from, on one day; a miss is one where the engine would accuse or flag it with the proof hidden.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.amberFg, marginBottom: 10, lineHeight: 1.5 }} data-testid="readiness-trust-note">
            A site's readiness rests on that merchant's own signed reports: a merchant controls its install and could inflate its own numbers, so this is evidence for a staff decision, not proof. Turning labels on is a Sill staff action only, and every change is recorded in the staff log.
          </div>
          {data.sites.length === 0 ? (
            <Card style={{ padding: 16 }}>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No site has verified agent traffic measured yet.</div>
            </Card>
          ) : data.sites.map((s) => (
            <SiteCard key={s.site_id} site={s} onChanged={() => { void load(); onChanged?.(); }} />
          ))}
        </>
      ) : null}
    </div>
  );
}
